import { Injectable } from '@angular/core';

import { ListResponse } from '../../models/list-response';
import {
  CustomerEntryPass,
  CustomerEntryPassFilters,
  EntryPass,
  EntryPassDiscount,
  EntryPassDiscountFilters,
  EntryPassEntryTimeSlot,
  EntryPassFilters, EntryPassGiftDiscount, EntryPassGiftDiscountFilters,
  EntryPassGiftingConfig
} from '../../models/entry-pass';

import { ApiService } from '../api/api.service';
import { AccountingConnection } from '../../models/organization-config';


@Injectable({
  providedIn: 'root'
})
export class EntryPassService {
  /**
   * A service providing access to entry passes via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of entry passes from the API.
   */
  getEntryPasses(pageNumber?: number,
                 pageSize?: number,
                 filters?: EntryPassFilters): Promise<ListResponse<EntryPass>> {
    return this.apiService.getCollection<EntryPass, EntryPassFilters>(
      'entry-passes/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get an individual entry pass from the API.
   */
  getEntryPass(id: number): Promise<EntryPass> {
    return this.apiService.getItem<EntryPass>(
      'entry-passes/',
      id
    );
  }

  /**
   * Post a new entry pass to the API.
   */
  addEntryPass(entryPass: EntryPass): Promise<EntryPass> {
    return this.apiService.postToAPI<EntryPass>(
      'entry-passes/',
      entryPass
    );
  }

  /**
   * Update an entry pass via the API.
   */
  updateEntryPass(entryPass: EntryPass): Promise<EntryPass> {
    return this.apiService.patchToAPI<EntryPass>(
      entryPass.url,
      entryPass
    );
  }

  /**
   * Update an entry pass's account code configuration via the API.
   */
  updateEntryPassRevenueAccount(conn: AccountingConnection,
                                entryPass: EntryPass,
                                accountCode: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/set-entry-pass-revenue-account-code/' + conn.id.toString() + '/' + entryPass.id.toString() + '/',
      {account_code: accountCode}
    ).then();
  }

  /**
   * Get a page of entry time slots for a given entry pass from the API.
   *
   * Only relevant for entry passes with has_limited_entry=true.
   */
  getEntryTimeSlots(entryPassId: number,
                    pageNumber?: number,
                    pageSize?: number): Promise<ListResponse<EntryPassEntryTimeSlot>> {
    return this.apiService.getCollectionNoFilters<EntryPassEntryTimeSlot>(
      'entry-passes/' + entryPassId.toString() + '/entry-time-slots/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new entry time slot to the API.
   *
   * Only relevant for entry passes with has_limited_entry=true.
   */
  addEntryTimeSlot(entryPassId: number,
                   entryTimeSlot: EntryPassEntryTimeSlot): Promise<EntryPassEntryTimeSlot> {
    // Treat empty string as undefined, so the error message shows that it was
    // not provided.
    if (entryTimeSlot.start_time === '') {
      entryTimeSlot.start_time = undefined;
    }
    if (entryTimeSlot.end_time === '') {
      entryTimeSlot.end_time = undefined;
    }

    return this.apiService.postToAPI<EntryPassEntryTimeSlot>(
      'entry-passes/' + entryPassId.toString() + '/entry-time-slots/',
      entryTimeSlot
    );
  }

  /**
   * Update a entry time slot by making a PUT request to the API.
   *
   * Only relevant for entry passes with has_limited_entry=true.
   */
  updateEntryTimeSlot(entryTimeSlot: EntryPassEntryTimeSlot): Promise<EntryPassEntryTimeSlot> {
    // Treat empty string as undefined, so the error message shows that it was
    // not provided.
    if (entryTimeSlot.start_time === '') {
      entryTimeSlot.start_time = undefined;
    }
    if (entryTimeSlot.end_time === '') {
      entryTimeSlot.end_time = undefined;
    }

    return this.apiService.putToAPI<EntryPassEntryTimeSlot>(
      entryTimeSlot.url,
      entryTimeSlot
    );
  }

  /**
   * Delete the given entry time slot.
   *
   * Only relevant for entry passes with has_limited_entry=true.
   */
  deleteEntryTimeSlot(entryTimeSlotUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      entryTimeSlotUrl
    ).then();
  }

  getEntryPassDiscounts(pageNumber?: number,
                        pageSize?: number,
                        filters?: EntryPassDiscountFilters): Promise<ListResponse<EntryPassDiscount>> {
    return this.apiService.getCollection<EntryPassDiscount, EntryPassDiscountFilters>(
      'entry-pass-discounts/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getEntryPassDiscount(id: number): Promise<EntryPassDiscount> {
    return this.apiService.getItem<EntryPassDiscount>(
      'entry-pass-discounts/',
      id
    );
  }

  addEntryPassDiscount(discount: EntryPassDiscount): Promise<EntryPassDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.postToAPI<EntryPassDiscount>(
      'entry-pass-discounts/',
      discount
    );
  }

  updateEntryPassDiscount(discount: EntryPassDiscount): Promise<EntryPassDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.patchToAPI<EntryPassDiscount>(
      discount.url,
      discount
    );
  }

  deleteEntryPassDiscount(discount: EntryPassDiscount): Promise<EntryPassDiscount> {
    return this.apiService.deleteFromAPI<EntryPassDiscount>(
      discount.url
    );
  }

  getEntryPassGiftingConfigs(): Promise<EntryPassGiftingConfig[]> {
    return this.apiService.getFromAPI<EntryPassGiftingConfig[]>(
      'entry-pass-gifting-configs/'
    );
  }

  getEntryPassGiftingConfig(id: number): Promise<EntryPassGiftingConfig> {
    return this.apiService.getItem<EntryPassGiftingConfig>(
      'entry-pass-gifting-configs/',
      id
    );
  }

  addEntryPassGiftingConfig(giftingConfig: EntryPassGiftingConfig): Promise<EntryPassGiftingConfig> {
    return this.apiService.postToAPI<EntryPassGiftingConfig>(
      'entry-pass-gifting-configs/',
      giftingConfig
    );
  }

  updateEntryPassGiftingConfig(giftingConfig: EntryPassGiftingConfig): Promise<EntryPassGiftingConfig> {
    return this.apiService.patchToAPI<EntryPassGiftingConfig>(
      giftingConfig.url,
      giftingConfig
    );
  }

  deleteEntryPassGiftingConfig(giftingConfig: EntryPassGiftingConfig): Promise<EntryPassGiftingConfig> {
    return this.apiService.deleteFromAPI<EntryPassGiftingConfig>(
      giftingConfig.url
    );
  }

  assignGiftingConfigToEntryPasses(giftingConfigId: number,
                                   entryPassIds: number[]): Promise<void> {
    return this.apiService.postToAPI<any>(
      'assign-gifting-config-to-entry-passes/',
      {
        gifting_config_id: giftingConfigId,
        entry_pass_ids: entryPassIds
      }
    ).then();
  }

  getEntryPassGiftDiscounts(pageNumber?: number,
                            pageSize?: number,
                            filters?: EntryPassGiftDiscountFilters): Promise<ListResponse<EntryPassGiftDiscount>> {
    return this.apiService.getCollection<EntryPassGiftDiscount, EntryPassGiftDiscountFilters>(
      'entry-pass-gift-discounts/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getEntryPassGiftDiscount(id: number): Promise<EntryPassGiftDiscount> {
    return this.apiService.getItem<EntryPassGiftDiscount>(
      'entry-pass-gift-discounts/',
      id
    );
  }

  addEntryPassGiftDiscount(discount: EntryPassGiftDiscount): Promise<EntryPassGiftDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.postToAPI<EntryPassGiftDiscount>(
      'entry-pass-gift-discounts/',
      discount
    );
  }

  updateEntryPassGiftDiscount(discount: EntryPassGiftDiscount): Promise<EntryPassGiftDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.patchToAPI<EntryPassGiftDiscount>(
      discount.url,
      discount
    );
  }

  deleteEntryPassGiftDiscount(discount: EntryPassGiftDiscount): Promise<EntryPassGiftDiscount> {
    return this.apiService.deleteFromAPI<EntryPassGiftDiscount>(
      discount.url
    );
  }

  /**
   * Get a page of customer entry passes from the API.
   */
  getCustomerEntryPasses(pageNumber?: number,
                         pageSize?: number,
                         filters?: CustomerEntryPassFilters): Promise<ListResponse<CustomerEntryPass>> {
    return this.apiService.getCollection<CustomerEntryPass, CustomerEntryPassFilters>(
      'customer-entry-passes/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }
}
